import { useState } from "react";
import {
  Building2, CheckCircle2, Circle, ShieldCheck, Fingerprint, ExternalLink, Gauge,
  Plus, Trash2, CreditCard, TrendingUp, Bell, Sparkles, AlertCircle, DollarSign,
  Landmark, ArrowRight, FileText, Download, RefreshCw, Check,
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Field } from "@/pages/formation/FormationSteps";

export function ProfileStep({ wf, credibility, toggle }) {
  const im = wf.imported;
  const items = wf.credibility.items;
  return (
    <div data-testid="credit-step-profile">
      {im.has_profile ? (
        <div className="rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#151310] to-[#121214] p-6 mb-5" data-testid="credit-imported">
          <div className="flex items-center gap-2 mb-3"><Building2 className="w-5 h-5 text-[#D4AF37]" /><h3 className="font-heading text-lg text-white">Imported from your Business Profile</h3></div>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {[["Business", im.name || "—"], ["Entity", im.entity || "—"], ["State", im.state || "—"], ["Industry", im.industry || "—"],
              ["EIN", im.ein || (im.has_ein ? "On file" : "Not set")], ["Owners", im.owners]].map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-white/5 py-1"><span className="text-[#71717A]">{k}</span><span className="text-white">{v}</span></div>
            ))}
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-[#D4AF37]/30 bg-[#151310] p-4 mb-5 text-sm text-[#D4D4D8]" data-testid="credit-no-profile">
          Tip: complete <span className="text-[#D4AF37]">Business Formation</span> first so we can auto-import your business details.
        </div>
      )}

      <div className="flex items-center gap-4 mb-4">
        <div className="relative w-20 h-20 shrink-0">
          <svg viewBox="0 0 36 36" className="w-20 h-20 -rotate-90">
            <circle cx="18" cy="18" r="15.9" fill="none" stroke="#ffffff12" strokeWidth="3" />
            <circle cx="18" cy="18" r="15.9" fill="none" stroke="#D4AF37" strokeWidth="3" strokeDasharray={`${wf.credibility.score}, 100`} strokeLinecap="round" />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center font-heading text-lg text-white" data-testid="credibility-score">{wf.credibility.score}%</div>
        </div>
        <div><div className="font-heading text-lg text-white">Business Credibility Score</div><div className="text-sm text-[#71717A]">{wf.credibility.done} of {wf.credibility.total} foundations complete. Finish these before building credit.</div></div>
      </div>

      <div className="space-y-2" data-testid="credibility-checklist">
        {items.map((it) => {
          const editable = it.source === "You";
          return (
            <button key={it.id} onClick={() => editable && toggle(it.id)} disabled={!editable} data-testid={`credibility-${it.id}`}
              className={`w-full text-left flex items-center gap-3 rounded-xl border p-3.5 transition-colors ${it.done ? "border-[#D4AF37]/25 bg-[#151310]" : "border-white/8 bg-[#121214]"} ${editable ? "hover:border-white/20 cursor-pointer" : "cursor-default"}`}>
              {it.done ? <CheckCircle2 className="w-5 h-5 text-[#D4AF37] shrink-0" /> : <Circle className="w-5 h-5 text-[#52525B] shrink-0" />}
              <span className="text-sm text-white flex-1">{it.label}</span>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-[#71717A]">{it.source}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function DunsStep({ duns, setDuns }) {
  return (
    <div data-testid="credit-step-duns">
      <p className="text-sm text-[#A1A1AA] mb-4">Your D-U-N-S Number is a free, unique identifier from Dun & Bradstreet — the foundation of your PAYDEX score.</p>
      <div className="flex gap-3 mb-5">
        {[["yes", "I have a D-U-N-S", true], ["no", "I need one", false]].map(([id, label, val]) => (
          <button key={id} onClick={() => setDuns({ ...duns, has_duns: val })} data-testid={`duns-has-${id}`}
            className={`flex-1 rounded-xl border p-4 text-sm font-medium transition-colors ${duns.has_duns === val ? "border-[#D4AF37]/50 bg-[#151310] text-white" : "border-white/8 bg-[#121214] text-[#A1A1AA] hover:border-white/20"}`}>{label}</button>
        ))}
      </div>
      {duns.has_duns === true && (
        <div className="max-w-sm"><Field label="D-U-N-S Number" value={duns.duns_number} onChange={(v) => setDuns({ ...duns, duns_number: v })} placeholder="12-345-6789" testid="duns-number" /></div>
      )}
      {duns.has_duns === false && (
        <div className="rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#151310] to-[#121214] p-6" data-testid="duns-guidance">
          <div className="flex items-center gap-2 mb-3"><Fingerprint className="w-5 h-5 text-[#D4AF37]" /><h3 className="font-heading text-lg text-white">Get your free D-U-N-S Number</h3></div>
          <ul className="space-y-2 text-sm text-[#A1A1AA] mb-4">
            <li className="flex gap-2"><Check className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" />Apply directly on the D&B website (free — allow 1–30 days).</li>
            <li className="flex gap-2"><Check className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" />Use your exact legal business name, address, and phone.</li>
            <li className="flex gap-2"><Check className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" />Once issued, come back and enter it to track your file.</li>
          </ul>
          <div className="flex flex-wrap items-center gap-3">
            <a href="https://www.dnb.com/duns-number/get-a-duns.html" target="_blank" rel="noreferrer" data-testid="duns-apply-link"
              className="inline-flex items-center gap-1.5 text-sm text-[#D4AF37] hover:underline">Apply on D&B <ExternalLink className="w-3.5 h-3.5" /></a>
            <button onClick={() => setDuns({ ...duns, status: "applied" })} data-testid="duns-mark-applied"
              className={`text-xs h-8 px-3 rounded-full border transition-colors ${duns.status === "applied" ? "border-[#D4AF37]/40 text-[#D4AF37] bg-[#151310]" : "border-white/15 text-white hover:bg-white/10"}`}>
              {duns.status === "applied" ? "Marked as applied ✓" : "Mark as applied"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function BureausStep({ bureaus, setBureaus }) {
  const CARDS = [
    { key: "paydex", name: "Dun & Bradstreet", metric: "PAYDEX (0–100)", max: 100, hint: "80+ = pays early" },
    { key: "experian", name: "Experian Business", metric: "Intelliscore (1–100)", max: 100, hint: "76+ = low risk" },
    { key: "equifax", name: "Equifax Business", metric: "Credit Risk (101–992)", max: 992, hint: "Higher is better" },
  ];
  return (
    <div data-testid="credit-step-bureaus">
      <p className="text-sm text-[#A1A1AA] mb-4">Track scores from the three business credit bureaus. Enter them as you receive reports — leave blank if not yet scored.</p>
      <div className="grid sm:grid-cols-3 gap-4">
        {CARDS.map((c) => (
          <div key={c.key} className="rounded-2xl border border-white/8 bg-[#121214] p-5" data-testid={`bureau-${c.key}`}>
            <div className="flex items-center gap-2 mb-1"><Gauge className="w-4 h-4 text-[#D4AF37]" /><span className="text-sm text-white font-medium">{c.name}</span></div>
            <div className="text-[11px] text-[#71717A] mb-3">{c.metric}</div>
            <input type="number" value={bureaus[c.key] ?? ""} onChange={(e) => setBureaus({ ...bureaus, [c.key]: e.target.value === "" ? null : Number(e.target.value) })}
              placeholder="—" data-testid={`bureau-input-${c.key}`}
              className="w-full rounded-xl bg-[#0d0d0f] border border-white/10 px-3 py-2.5 text-lg font-heading text-white text-center focus:outline-none focus:border-[#D4AF37]/40" />
            <div className="text-[11px] text-[#52525B] mt-2">{c.hint}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function VendorsStep({ wf, onAdd, onDelete, onPayment }) {
  const [form, setForm] = useState({ vendor_name: "", account_type: "Net 30", credit_limit: "", balance: "", status: "applied", reports_to: "", next_payment_date: "" });
  const [showForm, setShowForm] = useState(false);
  const accounts = wf.vendor_accounts || [];

  const submit = () => {
    if (!form.vendor_name) return;
    onAdd({ ...form, credit_limit: parseFloat(form.credit_limit) || 0, balance: parseFloat(form.balance) || 0 });
    setForm({ vendor_name: "", account_type: "Net 30", credit_limit: "", balance: "", status: "applied", reports_to: "", next_payment_date: "" });
    setShowForm(false);
  };

  return (
    <div data-testid="credit-step-vendors">
      <p className="text-sm text-[#A1A1AA] mb-4">Open accounts that report to the bureaus, in order. Add each account to track limits, balances, and payment reporting.</p>

      <div className="rounded-2xl border border-white/8 bg-[#121214] p-5 mb-5" data-testid="recommended-vendors">
        <div className="flex items-center gap-2 mb-3"><TrendingUp className="w-4 h-4 text-[#D4AF37]" /><span className="text-sm font-semibold text-white">Recommended order</span></div>
        <div className="space-y-2">
          {wf.recommended_vendors.map((v, i) => (
            <div key={i} className="flex items-center gap-3 text-sm" data-testid={`rec-vendor-${i}`}>
              <span className="w-6 h-6 rounded-full bg-[#D4AF37]/10 text-[#D4AF37] text-xs flex items-center justify-center shrink-0">{v.stage}</span>
              <span className="text-white flex-1">{v.name} <span className="text-[#71717A]">· {v.type} · {v.category}</span></span>
              <span className="text-[10px] text-[#52525B] shrink-0">{v.reports_to}</span>
              <button onClick={() => { setForm((f) => ({ ...f, vendor_name: v.name, account_type: v.type === "Store Credit" ? "Store Credit" : v.type, reports_to: v.reports_to })); setShowForm(true); }}
                data-testid={`rec-add-${i}`} className="text-xs text-[#D4AF37] hover:underline shrink-0">Add</button>
            </div>
          ))}
        </div>
      </div>

      {!showForm && (
        <button onClick={() => setShowForm(true)} data-testid="vendor-add-btn"
          className="flex items-center gap-1.5 text-sm h-10 px-4 rounded-full bg-white/5 border border-white/15 text-white hover:bg-white/10 transition-colors mb-5">
          <Plus className="w-4 h-4" /> Add vendor account
        </button>
      )}
      {showForm && (
        <div className="rounded-2xl border border-[#D4AF37]/25 bg-[#151310] p-5 mb-5" data-testid="vendor-form">
          <div className="grid sm:grid-cols-2 gap-3 mb-3">
            <Field label="Vendor name" value={form.vendor_name} onChange={(v) => setForm({ ...form, vendor_name: v })} testid="vendor-name-input" />
            <label className="block"><span className="text-xs text-[#A1A1AA] mb-1.5 block">Account type</span>
              <Select value={form.account_type} onValueChange={(v) => setForm({ ...form, account_type: v })}>
                <SelectTrigger className="bg-[#0d0d0f] border-white/10 text-white h-11" data-testid="vendor-type-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1C1C1F] border-white/10 text-white">{wf.account_types.map((t) => <SelectItem key={t} value={t} className="focus:bg-white/10">{t}</SelectItem>)}</SelectContent>
              </Select>
            </label>
            <Field label="Credit limit ($)" type="number" value={form.credit_limit} onChange={(v) => setForm({ ...form, credit_limit: v })} testid="vendor-limit-input" />
            <Field label="Balance ($)" type="number" value={form.balance} onChange={(v) => setForm({ ...form, balance: v })} testid="vendor-balance-input" />
            <label className="block"><span className="text-xs text-[#A1A1AA] mb-1.5 block">Status</span>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="bg-[#0d0d0f] border-white/10 text-white h-11" data-testid="vendor-status-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1C1C1F] border-white/10 text-white">{["applied", "approved", "reporting"].map((t) => <SelectItem key={t} value={t} className="focus:bg-white/10">{t}</SelectItem>)}</SelectContent>
              </Select>
            </label>
            <Field label="Next payment date" type="date" value={form.next_payment_date} onChange={(v) => setForm({ ...form, next_payment_date: v })} testid="vendor-nextpay-input" />
          </div>
          <div className="flex gap-2">
            <button onClick={submit} data-testid="vendor-save-btn" className="fv-gold-gradient text-[#0A0A0B] rounded-full px-5 py-2 text-sm font-semibold">Save account</button>
            <button onClick={() => setShowForm(false)} className="rounded-full px-4 py-2 text-sm text-[#A1A1AA] hover:text-white">Cancel</button>
          </div>
        </div>
      )}

      <div className="space-y-2" data-testid="vendor-accounts-list">
        {accounts.length === 0 && <p className="text-sm text-[#52525B]">No vendor accounts yet.</p>}
        {accounts.map((a) => (
          <div key={a.account_id} className="rounded-xl border border-white/8 bg-[#121214] p-4" data-testid={`vendor-account-${a.account_id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <CreditCard className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-white font-medium">{a.vendor_name}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#D4AF37]/10 text-[#D4AF37]">{a.account_type}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${a.status === "reporting" ? "bg-emerald-500/15 text-emerald-400" : a.status === "approved" ? "bg-blue-500/15 text-blue-400" : "bg-white/5 text-[#71717A]"}`}>{a.status}</span>
                  </div>
                  <div className="text-xs text-[#71717A] mt-1">Limit ${a.credit_limit || 0} · Balance ${a.balance || 0}{a.reports_to && ` · ${a.reports_to}`}{a.next_payment_date && ` · Due ${a.next_payment_date}`}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => onPayment(a)} data-testid={`vendor-pay-${a.account_id}`} className="flex items-center gap-1 text-xs h-8 px-3 rounded-full bg-white/5 border border-white/15 text-white hover:bg-white/10 transition-colors"><DollarSign className="w-3 h-3" /> Pay</button>
                <button onClick={() => onDelete(a.account_id)} data-testid={`vendor-delete-${a.account_id}`} className="text-[#71717A] hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function TimelineStep({ wf }) {
  const tl = wf.timeline;
  const fr = wf.funding_readiness;
  const reminders = wf.payment_reminders || [];
  return (
    <div data-testid="credit-step-timeline">
      <div className="grid sm:grid-cols-3 gap-3 mb-5">
        <div className="rounded-xl border border-white/8 bg-[#121214] p-4"><Gauge className="w-4 h-4 text-[#D4AF37] mb-2" /><div className="font-heading text-xl text-white" data-testid="timeline-credit-score">{wf.credit_score.score}</div><div className="text-[11px] text-[#71717A]">Business credit score ({wf.credit_score.tier})</div></div>
        <div className="rounded-xl border border-white/8 bg-[#121214] p-4"><TrendingUp className="w-4 h-4 text-[#D4AF37] mb-2" /><div className="font-heading text-xl text-white">{fr.pct}%</div><div className="text-[11px] text-[#71717A]">Funding readiness</div></div>
        <div className="rounded-xl border border-white/8 bg-[#121214] p-4"><CheckCircle2 className="w-4 h-4 text-[#D4AF37] mb-2" /><div className="font-heading text-xl text-white">~{tl.est_days_to_ready}d</div><div className="text-[11px] text-[#71717A]">Est. to funding ready</div></div>
      </div>

      {reminders.length > 0 && (
        <div className="rounded-2xl border border-[#D4AF37]/25 bg-[#151310] p-4 mb-5" data-testid="payment-reminders">
          <div className="flex items-center gap-2 mb-2"><Bell className="w-4 h-4 text-[#D4AF37]" /><span className="text-sm font-semibold text-white">Payment reminders</span></div>
          {reminders.map((r) => (
            <div key={r.account_id} className="flex items-center justify-between text-sm py-1" data-testid={`reminder-${r.account_id}`}>
              <span className="text-white">{r.vendor_name} <span className="text-[#71717A]">· ${r.balance}</span></span>
              <span className={r.overdue ? "text-red-400" : "text-[#D4AF37]"}>{r.overdue ? `Overdue by ${Math.abs(r.days_until)}d` : `Due in ${r.days_until}d`}</span>
            </div>
          ))}
        </div>
      )}

      <h3 className="font-heading text-lg text-white mb-1">Credit Building Timeline</h3>
      <p className="text-sm text-[#71717A] mb-4">Current stage: <span className="text-[#D4AF37]">{tl.current_stage}</span></p>
      <div className="space-y-2" data-testid="timeline-milestones">
        {tl.items.map((m) => (
          <div key={m.id} className={`flex items-start gap-3 rounded-xl border p-4 ${m.completed ? "border-[#D4AF37]/25 bg-[#151310]" : "border-white/8 bg-[#121214]"}`} data-testid={`milestone-${m.id}`}>
            {m.completed ? <CheckCircle2 className="w-5 h-5 text-[#D4AF37] shrink-0" /> : <Circle className="w-5 h-5 text-[#52525B] shrink-0" />}
            <div><div className="text-sm text-white">{m.title}</div><div className="text-xs text-[#71717A]">{m.detail}</div></div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function CreditDocumentsStep({ hasDocs, onGenerate, generating }) {
  const DOCS = ["Business Credit Checklist", "Vendor Tracker", "Credit Building Roadmap", "Funding Readiness Report", "Business Credibility Report"];
  return (
    <div data-testid="credit-step-documents">
      <p className="text-sm text-[#A1A1AA] mb-4">Generate your business credit documents — saved automatically to your Document Library.</p>
      <div className="grid sm:grid-cols-2 gap-2 mb-5">
        {DOCS.map((d) => (
          <div key={d} className="flex items-center gap-3 rounded-xl border border-white/8 bg-[#121214] p-4" data-testid={`credit-gendoc-${d.replace(/\s+/g, '-').toLowerCase()}`}>
            <FileText className={`w-4 h-4 shrink-0 ${hasDocs ? "text-[#D4AF37]" : "text-[#52525B]"}`} />
            <span className="text-sm text-[#D4D4D8] flex-1">{d}</span>
            {hasDocs && <Check className="w-4 h-4 text-[#D4AF37]" />}
          </div>
        ))}
      </div>
      <button onClick={onGenerate} disabled={generating} data-testid="credit-generate-documents-btn"
        className="flex items-center gap-2 fv-gold-gradient text-[#0A0A0B] rounded-full px-5 py-2.5 text-sm font-semibold disabled:opacity-50">
        {generating ? "Generating…" : hasDocs ? <><RefreshCw className="w-4 h-4" /> Regenerate</> : <><Download className="w-4 h-4" /> Generate documents</>}
      </button>
      {hasDocs && <p className="text-xs text-emerald-400 mt-3" data-testid="credit-documents-saved-note">✓ Saved to your Document Library.</p>}
    </div>
  );
}

export function CreditReviewStep({ review, onComplete, completing }) {
  return (
    <div data-testid="credit-step-review">
      <div className="rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#151310] to-[#121214] p-6 mb-4 flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl fv-gold-gradient flex items-center justify-center shrink-0"><Sparkles className="w-6 h-6 text-[#0A0A0B]" /></div>
        <div><div className="font-heading text-lg text-white">AI credit review</div><div className="text-sm text-[#A1A1AA]">Funding readiness: <span className="text-[#D4AF37] font-semibold" data-testid="credit-review-readiness">{review.funding_readiness}%</span> · Credit score: {review.credit_score}</div></div>
      </div>
      {review.weaknesses.length > 0 && <ReviewBlock title="Weaknesses to address" icon={AlertCircle} color="text-[#D4AF37]" items={review.weaknesses} testid="review-weaknesses" />}
      {review.next_accounts.length > 0 && <ReviewBlock title="Open these next" icon={ArrowRight} color="text-emerald-400" items={review.next_accounts.map((n) => `${n.title} — ${n.why}`)} testid="review-next-accounts" />}
      {review.recommendations.length > 0 && <ReviewBlock title="Recommendations" icon={CheckCircle2} color="text-blue-400" items={review.recommendations.map((r) => `${r.title} — ${r.detail}`)} testid="review-recs" />}
      <button onClick={onComplete} disabled={completing} data-testid="credit-complete-btn"
        className="mt-4 flex items-center gap-2 fv-gold-gradient text-[#0A0A0B] rounded-full px-6 py-3 text-sm font-semibold disabled:opacity-50">
        {completing ? "Finishing…" : <>Finish & generate funding plan <ArrowRight className="w-4 h-4" /></>}
      </button>
    </div>
  );
}

function ReviewBlock({ title, icon: Icon, color, items, testid }) {
  return (
    <div className="rounded-xl border border-white/8 bg-[#121214] p-5 mb-3" data-testid={testid}>
      <div className={`flex items-center gap-2 mb-2 ${color}`}><Icon className="w-4 h-4" /><span className="text-sm font-semibold">{title}</span></div>
      <ul className="space-y-1.5">{items.map((it, i) => <li key={i} className="flex items-start gap-2 text-sm text-[#A1A1AA]"><Circle className="w-1.5 h-1.5 mt-1.5 shrink-0 fill-current" />{it}</li>)}</ul>
    </div>
  );
}

export { Landmark, Progress };
